import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Back, Bounce, Circ, Elastic, Expo, Power3, Sine, TimelineLite, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import { list } from "Utils/Reflection";
// @ts-ignore
import {BezierPlugin} from "gsap/BezierPlugin";

import { ch8_p06 } from "../../../layouts/ch8_p06/ch8_p06";

const dontTreeShake = [BezierPlugin];

export class Chapter8Page6 extends AbstractStoryState implements ReturnType<typeof ch8_p06> {

    readonly bg = this.addChild(new SceneSprite());
    readonly water = this.addChild(new SceneSprite());
    readonly tree = this.addChild(new SceneSprite());
    readonly emptyTire = this.addChild(new SceneSprite());
    readonly lambInTire = this.addChild(new SceneSprite());
    readonly treeTop = this.addChild(new SceneSprite());
    readonly holly = [
        this.addChild(new SceneSprite()),
        this.addChild(new SceneSprite()),
    ];
    readonly lambInAir = [
        this.addChild(new SceneSprite()),
        this.addChild(new SceneSprite()),
    ];
    readonly waterFront = this.addChild(new SceneSprite());

    protected lambTimeline?: TimelineMax;

    protected animateLambInAir: boolean = false;
    protected elapsedTime: number = 0;

    public init(pSize: PIXI.Point, pData: any) {
        // exported layouts from Animate CC
        ch8_p06.call(this);

        this.holly[0].visible = false;
        this.holly[1].visible = true; // array is in reverse order from how these are used
        this.lambInAir.forEach(l=>l.visible = false);

        this.lambInTire.pivot.y = -(this.emptyTire.height - this.lambInTire.height) * 2;
        this.lambInTire.position.y += this.lambInTire.pivot.y / 2;

        this.lambTimeline = new TimelineMax();
        this.lambTimeline.fromTo([this.emptyTire, this.lambInTire], 2,
            {pixi: {angle: -45}},
            {pixi: {angle: 45}, ease: Sine.easeInOut},
        );
        this.lambTimeline.fromTo([this.emptyTire, this.lambInTire], 2,
            {pixi: {angle: 45}},
            {pixi: {angle: -45}, ease: Sine.easeInOut},
        );
        this.lambTimeline.repeat(-1).yoyo(true).progress(0.5);

        // super.init calls onResize
        super.init(pSize, pData);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.elapsedTime += pDeltaTime;
        const fps = 4;
        if (this.animateLambInAir) {
            if (this.elapsedTime > (1 / fps)) {
                this.lambInAir.push(this.lambInAir.shift()!);
                this.lambInAir.forEach(s=>s.visible = false);
                this.lambInAir[0].visible = true;
                this.elapsedTime = 0;
            }
        }
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
    }

    public animateOut(pOnComplete: () => void) {
        super.animateOut(pOnComplete);
    }

    public onAnimateInComplete() {
        super.onAnimateInComplete();

        this.lambInTire.interactive = true;
        this.lambInTire.buttonMode = true;
        this.lambInTire.accessible = true;
        this.lambInTire.addListener(HLF.Input.Events.POINTER_TAP, this.onLambClicked.bind(this));
    }

    protected onLambClicked() {
        this.lambTimeline?.kill();
        this.lambTimeline?.clear();
        this.lambTimeline?.repeat(0);
        this.lambTimeline?.yoyo(false);

        this.lambInTire.interactive = false;
        this.lambInTire.buttonMode = false;
        this.lambInTire.accessible = false;

        const lambInAirTargetPos = this.lambInAir[0].position.clone(); // from PSD

        // bit of a hack but I'm bad at vector math / nested transforms
        this.lambInTire.visible = false;
        this.lambInAir.forEach((l, i)=>{
            l.setParent(this.lambInTire);
            l.position.set(0, 0);
            HLF.PixiUtils.setParent(l, this);
            l.visible = i===0;
        });
        this.animateLambInAir = true;

        this.lambTimeline?.to(this.emptyTire, 6,
            {pixi: {angle: 0}, ease: Elastic.easeOut.config(0.5, 0.2)},
        );
        this.lambInAir.forEach(l=>{
            this.lambTimeline?.to(l.position, 3, {
                bezier: {
                    type: "soft",
                    values: [
                        {x: (lambInAirTargetPos.x + l.x) / 2, y: l.y - 200},
                        {x: lambInAirTargetPos.x, y: lambInAirTargetPos.y},
                    ],
                },
                ease: Power3.easeOut,
            }, 0);
        })
        this.lambTimeline?.call(()=>{
            this.holly[1].FadeOut();
            this.holly[0].FadeIn();
            this.animateLambInAir = false;
            this.lambInAir.forEach(l=>{
                if(l.visible) {
                    l.FadeOut(); // fade out lamb while still doing the position tween
                }
            });
        }, undefined, undefined, 1.5);
        this.lambTimeline?.call(()=>{
            this.gotoNextState();
        }, undefined, undefined, 4.5);

    }
}